import type { ThemeMeta, ThemeTokens } from '@/previewThemes/meta';

/**
 * Pawly's design constants.
 *
 * A friendly pet-supplies shop: a warm cream page, product pictures on big
 * rounded oat tiles, deep pine-teal buttons and a single tangerine note on the
 * sale lines and the wordmark's paw. Headings are set in a tall condensed
 * face, in capitals, the way a pet-food pack shouts its flavour.
 *
 * The shop it is previewed with is a pet store stocked with the platform's
 * own Pet Supplies library pictures - see `catalog.ts` for why it ships its
 * own, and `photos.ts` for where each picture came from.
 */
export const tokens: ThemeTokens = {
  // Tall, narrow and very heavy at its one weight: a section title in capitals
  // reads as a label on a bag of kibble without running to two lines.
  headingFont: 'Anton',
  // Tangerine. The primary in `look` is the teal, so a merchant who repaints
  // their primary moves the buttons; this is the note the theme keeps for its
  // own details - the sale line, the badge, the paw after the wordmark.
  accent: '#ff8a3d',
  monoFont: null,
  headingTracking: '0.01em',
  headingTransform: 'uppercase',
  // Left-aligned: the section titles sit over the left edge of a row of
  // tiles, as they do on a pet-food aisle's shelf labels.
  headingAlign: 'left',
  // Stars under every card: a pet owner picks a food by what other owners'
  // dogs and cats made of it.
  cardRatings: true,
  containerMax: '84rem',
  // A touch more air than the base: the rounded tiles want room between them.
  spaceScale: 1.05,
  // The teal rail and one cream deck.
  headerHeight: '6.75rem',
};

export const meta: ThemeMeta = {
  label: 'Pawly',
  hint: 'A warm pet-supplies shop: cream pages, rounded oat tiles, pine-teal buttons and tall condensed capitals.',
  industries: ['pet_supplies'],
  look: {
    colors: {
      // Deep pine teal as the primary: a solid button paints its label in the
      // page colour, so the buttons come out teal with cream type.
      primary: '#1f5f5b',
      background: '#fffdf8',
      // Oat: the ground behind every product picture and the footer's band.
      surface: '#fdf0dc',
      text: '#1b1b1b',
      muted: '#6f6a62',
    },
    typography: {
      font_family: 'Inter',
      heading_font: 'Anton',
      heading_weight: '700',
    },
    // Pills: soft, round controls, and the same radius rounds every tile.
    buttons: { radius_px: 32, style: 'solid' },
    // No `catalog` named: this theme ships its own in `catalog.ts`.
  },
};
